/**
 * 네이티브 셸 브리지 (v12 · N2).
 *
 * ── 셸 판정은 **브리지의 유무**로 한다 ──────────────────────
 *
 * UA 를 고치지 않는다 (`docs/29` D14). WKWebView 의 UA 에는 `iPhone` 이 들어 있어
 * `detectClientType` 이 `mobile_web` 을 주고 **그것이 맞는 값이다** — 손에 든 기기가 곧
 * 구매 기기이므로 Tier 0 이 맞고 버튼 문구도 `구매하기` 가 맞다. 표식을 붙이고 싶어지는
 * 자리이지만 붙이면 `client-type.ts` 의 픽스처 12종이 통째로 의미를 잃는다.
 *
 * ── 표면은 여섯이고, 세는 것은 수가 아니라 기준이다 (D13 · v17 개정) ──
 *
 * ```
 * platform · openExternal                          ← N2
 * requestPush · getPushRegistration · onDeepLink   ← N5
 * signIn                                           ← v17 (R3)
 * ```
 *
 * **표면을 늘리는 근거는 하나뿐이다 — 웹이 원리적으로 할 수 없는 플랫폼 능력.**
 * 「웹이 할 수 있는데 네이티브가 더 잘한다」는 근거가 되지 않는다. 늘어나면 웹과
 * 네이티브가 서로의 사정을 알게 되고, 그 순간 2단계(Android)가 「셸 하나를 더 쓰는 일」이
 * 아니라 **「웹을 다시 쓰는 일」**이 된다. `signIn` 이 그 기준을 통과하는 이유는 **구글이
 * 임베디드 user-agent 의 OAuth 를 차단하기 때문**이고, Android 는 Chrome Custom Tabs 로
 * **같은 이름이 대칭으로 선다** — 그 대칭이 서지 않는 이름은 표면이 될 수 없다.
 *
 * **홈 화면 PWA 가 이 기준의 반례가 아니라 증거다** — 웹이 할 수 있는 곳에서는 웹이 하고
 * (GIS redirect 모드, `docs/40` D11), 못 하는 곳에서만 표면이 는다.
 *
 * **메시지 핸들러 이름은 둘이지만 표면은 그보다 많다.** `openExternal` 은 동기 `boolean`
 * 이고(아래 주석) 나머지 둘은 `Promise` 라, 후자는 `WKScriptMessageHandlerWithReply`
 * (iOS 14+)를 쓴다 — 그 핸들러에 붙은 `postMessage` 는 **언제나 `Promise` 를 돌려주므로**
 * 동기 반환과 한 이름에 담을 수 없다. D13 이 세는 것은 웹이 보는 메서드이지 핸들러
 * 이름이 아니다.
 *
 * ── 판정이 순수 함수인 이유 ─────────────────────────────────
 *
 * 이 패키지는 node 환경에서 테스트한다(jsdom 미도입). `window` 를 읽는 것은
 * `getNativeBridge()` 한 줄이고 검사는 `readNativeBridge(raw)` 가 받는다 —
 * `client-type.ts`·`routing.ts`·`channel-selection.ts` 와 같은 자리다.
 */

import {
  isNativePushEnvironment,
  isNativePushProvider,
  type NativePushRegistration,
} from '@apm/contracts';

/** 셸이 도는 플랫폼. 2단계에서 `android` 가 는다 (`docs/29` §13.2). */
export const NATIVE_PLATFORMS = ['ios'] as const;
export type NativePlatform = (typeof NATIVE_PLATFORMS)[number];

/**
 * 셸의 로그인 결과 (v17 R3 · `docs/40` D4).
 *
 * **`null` 하나로 합치지 않는다.** `requestPush` 는 실패 이유를 합쳐도 됐고 그 판단이
 * 옳았다 — 「iOS 의 거부는 되돌릴 수 없고 등록 실패도 사용자가 할 일이 같다」.
 * **로그인은 할 일이 셋 다 다르다**: 취소는 아무 말도 하지 않고, 실패는 다시 시도하고,
 * 낡은 셸은 앱을 다시 깔아야 한다(그 셋째는 `signIn` 의 부재가 답한다).
 *
 * **플랫폼 중립이다.** iOS 용어가 하나도 없어야 2단계(Android)에서 같은 이름이 그대로
 * 선다 — 그것이 여섯째 표면을 허락한 기준(D2)의 유일한 방어선이다.
 */
export type NativeSignIn =
  { status: 'ok'; idToken: string } | { status: 'cancelled' } | { status: 'failed' };

/**
 * 셸이 준 답을 좁힌다. **모르는 모양은 `failed` 다.**
 *
 * `null` 을 그대로 흘리면 호출부가 넷째 갈래를 잊는다. 그리고 **빈 `idToken` 도
 * `failed` 로 좁힌다** — 그대로 보내면 서버가 401 을 주고, 화면 문구는 「테스트 사용자로
 * 등록돼 있는지 확인해 주세요」라 **원인을 반대로 가리킨다**(`docs/40` §8-③).
 */
export function readNativeSignIn(raw: unknown): NativeSignIn {
  if (typeof raw !== 'object' || raw === null) return { status: 'failed' };
  const c = raw as Record<string, unknown>;
  if (c.status === 'cancelled') return { status: 'cancelled' };
  if (c.status !== 'ok') return { status: 'failed' };
  if (typeof c.idToken !== 'string' || c.idToken === '') return { status: 'failed' };
  return { status: 'ok', idToken: c.idToken };
}

/** 네이티브 셸이 웹에 여는 통로. **여섯이고, 늘리는 기준은 위 머리 주석에 있다** (D13). */
export interface NativeBridge {
  readonly platform: NativePlatform;
  /**
   * 외부 URL 을 셸 밖(Safari)으로 연다.
   *
   * @returns **셸이 받았는가.** `docs/29` D13 의 원안은 `void` 였는데, 그러면 메시지
   *   핸들러가 없을 때 **아무 일도 일어나지 않는 버튼**이 된다 — 호출부는 열렸다고
   *   믿고 폴백을 거치지 않는다. 받지 못했으면 `false` 를 주고, 그때 호출부가 실제
   *   이동으로 내려가면 `WKNavigationDelegate` 가 그것을 가로채 Safari 로 연다.
   *   **침묵을 없애는 것이 이 반환값의 전부다.**
   */
  openExternal(url: string): boolean;
  /**
   * 알림 권한을 묻고 APNs 에 등록한다. **사용자 제스처 안에서만 부를 것** (WB-07).
   *
   * @returns 토큰까지 받았으면 등록 정보, 거부·실패면 `null`.
   *   **왜 이유를 나누지 않는가** — iOS 의 거부는 되돌릴 수 없고(설정 앱으로만 켠다)
   *   등록 실패도 사용자가 할 일이 같다. 화면이 갈라야 하는 것은
   *   `getPushRegistration()` 이 주는 「이미 켜져 있는가」 하나다.
   */
  requestPush(): Promise<NativePushRegistration | null>;
  /**
   * 이미 등록돼 있으면 그 토큰. **권한을 요청하지 않는다.**
   *
   * 부팅마다 부른다 — APNs 토큰은 앱 재설치·기기 복원에서 갈리고, 옛 토큰으로 보내면
   * **410 Unregistered** 다 (D11). 웹의 「구독 키 대조」에 대응하는 자리이고 증상도 같다:
   * **알림만 조용히 오지 않는다** (절대 규칙 19).
   */
  getPushRegistration(): Promise<NativePushRegistration | null>;
  /**
   * 알림 탭으로 들어온 딥링크를 받는다.
   *
   * **핸들러가 붙기 전에 온 링크를 버리지 않는다** (`docs/29` 함정 ⑪). 알림 탭은 앱을
   * **차갑게** 깨울 수 있어 그때는 WebView 도 이 핸들러도 아직 없다 — 셸이 마지막 하나를
   * 보관했다가 붙는 순간 넘긴다.
   */
  onDeepLink(handler: (url: string) => void): void;
  /**
   * 시스템 브라우저로 구글에 로그인하고 `id_token` 을 받는다 (v17 · `docs/40` D1).
   *
   * **`null` 일 수 있고, 그것이 이 표면의 전부다** (`docs/40` D3). 셸은 손으로 설치하는
   * 물건이라 **낡은 것이 오래 산다** — 이 이름을 `readNativeBridge` 의 필수 조건에 넣으면
   * 이미 폰에 깔린 셸(다섯만 심는다)이 **브리지 아님으로 떨어지고**, 그 기기에는 웹 Push 가
   * 원리적으로 없어 폴백도 없다. 게다가 서버는 거절한 구독을 지우지 않으므로 **낡은
   * `nativeToken` 이 남아 콘솔도 매칭도 「받을 수 있다」고 말한다**(`docs/40` §8-①).
   * userScript 의 객체는 `Object.freeze` 라 **웹이 나중에 채워 넣을 수도 없다.**
   *
   * 위 「반쪽짜리 브리지를 받지 않는다」와 어긋나지 않는다 — 그 규칙이 막는 것은 **증상 없는
   * 결핍**인데, 이 이름의 결핍은 **「이 앱으로는 로그인할 수 없습니다」라는 문장**으로 끝난다.
   *
   * **상한을 두지 않는다** (`docs/40` D9). `settleWithin` 의 근거는 「부팅이 대기에 걸리면
   * 전부 죽는다」인데 로그인은 부팅 경로가 아니고 **사람이 비밀번호를 치는 시간**이다 —
   * 상한을 두면 **성공한 로그인을 실패로 그린다.** 대신 셸이 모든 갈래에서 답하는 것이
   * 유일한 방어선이 된다(취소·실패 포함).
   */
  readonly signIn: (() => Promise<NativeSignIn>) | null;
}

function isNativePlatform(v: unknown): v is NativePlatform {
  return (NATIVE_PLATFORMS as readonly unknown[]).includes(v);
}

/**
 * 셸이 준 등록 정보를 계약(`NativePushRegistration`)으로 좁힌다.
 *
 * **셸을 믿지 않는다.** 넘어오는 것은 JS 객체이고 타입 검사가 닿지 않는 경계다 —
 * 모양이 틀린 채 그대로 기기 등록에 실으면 서버의 `ValidationPipe` 가
 * `forbidNonWhitelisted` 로 **요청 전체를 400** 으로 돌려주고(v9 함정 ②), 그러면
 * 구독이 만들어지지 않아 **알림이 아무에게도 가지 않는데 화면에는 증상이 없다.**
 * 여기서 걸러 내면 그 실패가 「등록 없음」이라는 관측 가능한 상태가 된다.
 */
export function readNativePushRegistration(raw: unknown): NativePushRegistration | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const c = raw as Record<string, unknown>;
  if (!isNativePushProvider(c.provider)) return null;
  if (typeof c.token !== 'string' || c.token === '') return null;
  if (!isNativePushEnvironment(c.environment)) return null;
  if (typeof c.bundleId !== 'string' || c.bundleId === '') return null;
  return {
    provider: c.provider,
    token: c.token,
    environment: c.environment,
    bundleId: c.bundleId,
  };
}

/**
 * 셸이 심어 둔 객체를 검사해 브리지로 받는다.
 *
 * **모양이 틀리면 `null` 이고 던지지 않는다.** 브리지가 깨진 셸에서도 앱은 웹 경로로
 * 그대로 돌아야 한다 — 여기서 던지면 **화면이 통째로 뜨지 않고**, 그 증상은 「앱이
 * 흰 화면이다」 하나라 원인이 드러나지 않는다.
 *
 * **다섯이 전부 있어야 브리지다.** 하나라도 없으면 셸이 아닌 것으로 본다 — 반쪽짜리
 * 브리지를 받아 두면 화면이 `bridge.requestPush` 를 옵셔널 체이닝으로 부르기 시작하고,
 * 그때부터 「셸인데 왜 알림이 안 되지」가 조용한 실패가 된다.
 */
export function readNativeBridge(raw: unknown): NativeBridge | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const c = raw as Record<string, unknown>;
  if (!isNativePlatform(c.platform)) return null;
  for (const name of ['openExternal', 'requestPush', 'getPushRegistration', 'onDeepLink']) {
    if (typeof c[name] !== 'function') return null;
  }

  const openExternal = c.openExternal as (url: string) => unknown;
  const requestPush = c.requestPush as () => unknown;
  const getPushRegistration = c.getPushRegistration as () => unknown;
  const onDeepLink = c.onDeepLink as (handler: (url: string) => void) => unknown;

  /**
   * 셸의 함수를 그대로 내주지 않고 감싼다. **던져도 앱이 죽지 않게** 하려는 것이고,
   * 이 통로가 막히는 것과 앱이 멈추는 것은 값이 다르다 — 앞엣것은 알림 하나를 잃고
   * 뒤엣것은 시청·구매를 통째로 잃는다.
   */
  const askShell = async (call: () => unknown): Promise<NativePushRegistration | null> => {
    try {
      return readNativePushRegistration(await call());
    } catch {
      return null;
    }
  };

  /**
   * **여섯째만 선택이다.** 필수 목록(위 `for`)에 넣지 않는 이유는 이 타입의 `signIn`
   * 주석에 있다 — 낡은 셸에서 브리지 판정이 통째로 뒤집히면 **Push 가 조용히 죽는다.**
   */
  const signIn = typeof c.signIn === 'function' ? (c.signIn as () => unknown) : null;

  return {
    platform: c.platform,
    signIn:
      signIn === null
        ? null
        : async () => {
            try {
              return readNativeSignIn(await signIn.call(raw));
            } catch {
              // 셸이 던졌다. `askShell` 과 같은 방침 — 이 통로가 막히는 것과 앱이 멈추는
              // 것은 값이 다르다.
              return { status: 'failed' as const };
            }
          },
    openExternal(url) {
      try {
        return openExternal.call(raw, url) === true;
      } catch {
        // 셸이 답하지 않았다. 호출부가 실제 이동으로 내려가고 델리게이트가 받는다.
        return false;
      }
    },
    requestPush: () => askShell(() => requestPush.call(raw)),
    getPushRegistration: () => askShell(() => getPushRegistration.call(raw)),
    onDeepLink(handler) {
      try {
        onDeepLink.call(raw, (url: unknown) => {
          if (typeof url === 'string' && url !== '') handler(url);
        });
      } catch {
        /* 셸이 받지 않았다. 알림 탭이 화면을 옮기지 못할 뿐 앱은 그대로 돈다 */
      }
    },
  };
}

/** DOM 을 읽는 유일한 줄. */
export function getNativeBridge(): NativeBridge | null {
  if (typeof window === 'undefined') return null;
  return readNativeBridge((window as { __bloomNative?: unknown }).__bloomNative);
}
